import { Mail, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer
      className="py-12 md:py-16 border-t-4 border-black bg-black text-white"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Grid - 1 col mobile, 2 col tablet, 4 col desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 xl:gap-12">
          {/* Brand - full width on tablet */}
          <div className="md:col-span-2 xl:col-span-1">
            <Link to="/" className="inline-flex flex-col items-center mb-4">
              <h3 className="font-heading font-bold text-2xl uppercase tracking-tight">
                BLACKLINE <span className="font-normal">ESTIMATING</span>
              </h3>
              <span className="text-[8px] sm:text-[9px] uppercase tracking-[0.08em] text-zinc-500 font-bold mt-0.5 text-center whitespace-nowrap">
                Cost Engineering · Tender Assurance
              </span>
            </Link>
            <p className="font-body text-zinc-400 text-sm leading-relaxed max-w-sm">
              Independent cost engineering for small residential and civil projects in New Zealand and Australia.
            </p>
          </div>

          {/* Pages */}
          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-4">
              Pages
            </h4>
            <div className="space-y-2">
              <Link
                to="/tender-red-review"
                className="block font-body text-sm text-zinc-400 hover:text-white transition-colors"
              >
                Tender Red Review
              </Link>
              <Link
                to="/services"
                className="block font-body text-sm text-zinc-400 hover:text-white transition-colors"
              >
                Estimating Services
              </Link>
              <Link
                to="/price-desk"
                className="block font-body text-sm text-zinc-400 hover:text-white transition-colors"
              >
                Price Desk
              </Link>
              <Link
                to="/about"
                className="block font-body text-sm text-zinc-400 hover:text-white transition-colors"
              >
                About
              </Link>
              <Link
                to="/blog"
                className="block font-body text-sm text-zinc-400 hover:text-white transition-colors"
              >
                Insights
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-4">
              Contact
            </h4>
            <div className="space-y-4">
              <div>
                <p className="font-body text-sm text-zinc-500 mb-1">Tender enquiries:</p>
                <Link
                  to="/tender-red-review#tender-form"
                  className="font-body text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  Submit through the secure review form
                </Link>
              </div>
              <div>
                <p className="font-body text-sm text-zinc-500 mb-1">General enquiries:</p>
                <a
                  href="mailto:info@blacklineestimating.com"
                  className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  <Mail size={16} strokeWidth={1.5} className="flex-shrink-0" />
                  <span className="font-mono text-sm">info@blacklineestimating.com</span>
                </a>
              </div>
            </div>
          </div>

          {/* Service Regions */}
          <div>
            <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-4">
              Service Regions
            </h4>
            <div className="space-y-2 font-body text-sm text-zinc-400">
              <p>New Zealand (NZ)</p>
              <p>Australia (AU)</p>
            </div>
            
            {/* Related Service - discreet link */}
            <div className="mt-6 pt-4 border-t border-zinc-800">
              <a
                href="https://meterback.com/construction"
                className="font-body text-xs text-zinc-400 hover:text-white transition-colors"
                data-testid="footer-meterback-link"
              >
                Project Cost Recovery — MeterBack
              </a>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 pt-8 border-t border-zinc-800">
          <p className="font-mono text-xs text-zinc-600 leading-relaxed max-w-4xl">
            Blackline Estimating provides construction cost engineering and tender assurance services. Our services do not replace project-specific professional advice from registered engineers, architects, quantity surveyors, or legal professionals. Blackline is not a registered QS practice or certified cost-engineering organisation. Independent commercial review only—not a guarantee of tender success or project profitability.
          </p>
        </div>

        {/* Bottom */}
        <div className="mt-8 pt-8 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-mono text-xs text-zinc-500">
            &copy; {currentYear} BLACKLINE ESTIMATING. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <Link
              to="/privacy"
              className="font-mono text-xs text-zinc-500 hover:text-white transition-colors"
            >
              Privacy
            </Link>
            <Link
              to="/terms"
              className="font-mono text-xs text-zinc-500 hover:text-white transition-colors"
            >
              Terms
            </Link>
            <Link
              to="/confidentiality"
              className="font-mono text-xs text-zinc-500 hover:text-white transition-colors"
            >
              Confidentiality
            </Link>
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="mt-8 flex justify-center">
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 px-6 py-3 border border-zinc-700 text-zinc-400 hover:text-white hover:border-white transition-colors group"
            data-testid="back-to-top"
          >
            <ArrowUp size={16} className="group-hover:-translate-y-1 transition-transform" />
            <span className="font-mono text-xs uppercase tracking-widest">Back to Top</span>
          </button>
        </div>
      </div>
    </footer>
  );
}
